import { cn } from '@shared/ui/cn';

import { PUBLISH_STATE_ORDER, resolveContentType, resolvePublishState } from '../_shared/constants';
import { copy } from './copy';

/**
 * The two single-select facets, and both of them are **buttons rather than
 * links**: the result is a state of this same route, not another document.
 *
 * Each is a `role="group"` of `aria-pressed` buttons, and a single-select
 * re-press keeps the chip active rather than clearing to an empty list — a
 * facet that can be pressed into nothing offers a view nobody asked for.
 *
 * **The three review states carry no chip**, and the line beneath says what the
 * absence means for the operator's work rather than why: `REVIEW_REQUIRED` is
 * `false`, so a submission transitions straight to published and nothing ever
 * enters those states. A chip reading zero would announce a state that does not
 * occur.
 *
 * **The chips carry no count.** The mockup draws one per chip, and no read on
 * this route returns per-state totals — a figure with no source is a figure the
 * surface would be inventing. The section head states shown-of-total from the
 * payload instead, which is the figure the operator can act on.
 */
export function ContentFacets({
  contentTypes,
  state,
  contentType,
  onState,
  onContentType,
}: {
  /** `CONTENT_TYPES`, read from the owning service — never a literal pair here. */
  contentTypes: readonly string[];
  state: string | null;
  contentType: string | null;
  onState: (next: string | null) => void;
  onContentType: (next: string | null) => void;
}) {
  return (
    <section aria-labelledby="content-facets" className="mb-6 grid gap-4">
      <h2 id="content-facets" className="sr-only">
        {copy.facets.label}
      </h2>

      <div role="group" aria-label={copy.facets.state} className="grid gap-2">
        <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.facets.state}</p>
        <div className="flex flex-wrap gap-2">
          <Chip pressed={state === null} onPress={() => onState(null)}>
            {copy.facets.allStates}
          </Chip>
          {PUBLISH_STATE_ORDER.map((member) => (
            <Chip key={member} pressed={state === member} onPress={() => onState(member)}>
              {resolvePublishState(member).label}
            </Chip>
          ))}
        </div>
      </div>

      <div role="group" aria-label={copy.facets.type} className="grid gap-2">
        <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.facets.type}</p>
        <div className="flex flex-wrap gap-2">
          <Chip pressed={contentType === null} onPress={() => onContentType(null)}>
            {copy.facets.allTypes}
          </Chip>
          {contentTypes.map((member) => (
            <Chip key={member} pressed={contentType === member} onPress={() => onContentType(member)}>
              {resolveContentType(member)}
            </Chip>
          ))}
        </div>
      </div>

      <p className="mb-0 max-w-measure text-xs text-ink-muted">{copy.facets.noReviewNote}</p>
    </section>
  );
}

function Chip({
  pressed,
  onPress,
  children,
}: {
  pressed: boolean;
  onPress: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onPress}
      className={cn(
        'hit-target rounded-pill border px-4 text-sm',
        pressed
          ? 'border-accent bg-accent-soft text-accent-text'
          : 'border-border bg-surface-raised text-ink-muted hover:bg-hover-bg',
      )}
    >
      {children}
    </button>
  );
}
